import { any, keys, map, pickAll, pipe, values } from 'lodash/fp'

import { OptimizationStatus } from '@/shared/generated'
import { PALETTE } from '@/shared/lib/theme-palette'

import { FINAL_FIELDS } from './constants'

export const getStatusScheme = (status?: OptimizationStatus) => {
	switch (status) {
		case OptimizationStatus.Finished:
			return {
				variant: 'success',
				color: PALETTE.green[100],
			}

		case OptimizationStatus.Canceled:
		case OptimizationStatus.New:
			return {
				variant: 'neutral',
				color: PALETTE.neutral[50],
			}

		case OptimizationStatus.Updating:
			return {
				variant: 'neutral',
				color: PALETTE.neutral[100],
			}

		case OptimizationStatus.Pending:
			return {
				variant: 'warning',
				color: PALETTE.orange[50],
			}

		case OptimizationStatus.UpdatingFailed:
		case OptimizationStatus.Failed:
			return {
				variant: 'destructive',
				color: PALETTE.red[50],
			}

		case OptimizationStatus.Locked:
			return {
				variant: 'info',
				color: PALETTE.blue[50],
			}

		default:
			return {
				variant: 'default',
				color: PALETTE.neutral[100],
			}
	}
}
export const getStatusBadgeVariant = (status?: OptimizationStatus) => {
	switch (status) {
		case OptimizationStatus.Finished:
			return 'success'

		case OptimizationStatus.Canceled:
		case OptimizationStatus.New:
			return 'neutral'

		case OptimizationStatus.Updating:
			return 'neutral'

		case OptimizationStatus.Pending:
			return 'warning'

		case OptimizationStatus.UpdatingFailed:
		case OptimizationStatus.Failed:
			return 'destructive'

		case OptimizationStatus.Locked:
			return 'info'

		default:
			return 'default'
	}
}
export const getStatusPlainColor = (status?: OptimizationStatus) => {
	switch (status) {
		case OptimizationStatus.Finished:
			return PALETTE.green[50]

		case OptimizationStatus.Canceled:
		case OptimizationStatus.New:
			return PALETTE.neutral[50]

		case OptimizationStatus.Updating:
			return PALETTE.neutral[100]

		case OptimizationStatus.Scheduled:
		case OptimizationStatus.Pending:
			return PALETTE.orange[50]

		case OptimizationStatus.UpdatingFailed:
		case OptimizationStatus.Failed:
			return PALETTE.red[50]

		case OptimizationStatus.Locked:
			return PALETTE.blue[50]

		default:
			return PALETTE.neutral[50]
	}
}
export const getOptStatus = (status?: OptimizationStatus) => {
	switch (status) {
		case OptimizationStatus.Canceled:
		case OptimizationStatus.Updating:
		case OptimizationStatus.New:
			return OptimizationStatus.New

		case OptimizationStatus.Scheduled:
			return OptimizationStatus.Scheduled

		case OptimizationStatus.UpdatingFailed:
		case OptimizationStatus.Failed:
			return OptimizationStatus.Failed

		default:
			return status
	}
}
export const getOgStatus = (
	status?: OptimizationStatus
): OptimizationStatus => {
	if (!status) {
		return OptimizationStatus.New
	}
	switch (status) {
		case OptimizationStatus.Canceled:
		case OptimizationStatus.New:
			return OptimizationStatus.New

		case OptimizationStatus.Scheduled:
			return OptimizationStatus.Scheduled

		default:
			return status
	}
}
export type AssociatedStatus =
	| OptimizationStatus.New
	| OptimizationStatus.Scheduled
	| OptimizationStatus.Pending
	| OptimizationStatus.Failed
	| OptimizationStatus.Finished
	| OptimizationStatus.Locked
export const getAssociatedStatus = (
	status: OptimizationStatus
): AssociatedStatus | OptimizationStatus => {
	switch (status) {
		case OptimizationStatus.Canceled:
		case OptimizationStatus.Updating:
			return OptimizationStatus.New
		case OptimizationStatus.Scheduled:
			return OptimizationStatus.Scheduled
		case OptimizationStatus.UpdatingFailed:
			return OptimizationStatus.Failed
		default:
			return status
	}
}

export const isEditableStatus = (status: OptimizationStatus) =>
	[
		OptimizationStatus.Finished,
		OptimizationStatus.New,
		OptimizationStatus.Failed,
		OptimizationStatus.Pending,
		OptimizationStatus.UpdatingFailed,
	].includes(status)

type ClientSpecificOptimizationTargetEntity = Record<string, any>
const entityHasFinalFields = (item: ClientSpecificOptimizationTargetEntity) =>
	any((field) => field in item, FINAL_FIELDS)
const entityHasNotFinalFields = (
	item: ClientSpecificOptimizationTargetEntity
) =>
	any((field) => field !== 'sku' && !FINAL_FIELDS.includes(field), keys(item))

export const isEditedFinalFields = pipe([values, any(entityHasFinalFields)])
export const isEditedModelFields = pipe([values, any(entityHasNotFinalFields)])
export const getOnlyFinalFields = map(pickAll(['sku', ...FINAL_FIELDS]))
